import React, { useState, useEffect } from 'react';
import { Card, CardHeader, Badge } from '../../../components/ui';
import {
  Building2, Users, Shield, RefreshCw,
  AlertTriangle, CheckCircle, XCircle
} from 'lucide-react';
import { adminService, DashboardGobernanza } from '../services/adminService';

/**
 * AdminDashboardPage — Gobernanza del sistema.
 * 
 * Solo métricas de PLATAFORMA:
 * - Bancas (total, activas, inactivas)
 * - Usuarios (total, activos, bloqueados)
 * - Suscripciones (activas, vencidas, próximas a vencer)
 * 
 * ❌ NO muestra ventas, tickets, jugadas, ni datos del negocio
 */
export const AdminDashboardPage: React.FC = () => {
  const [data, setData] = useState<DashboardGobernanza | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = () => {
    setLoading(true);
    setError('');
    adminService.getDashboard()
      .then(setData)
      .catch(err => setError(err.message || 'Error cargando dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Panel de Gobernanza</h1>
          <p className="text-gray-500">Administración de la plataforma TiemPos</p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          title="Refrescar"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <XCircle className="text-danger-600" size={20} />
            <p className="text-danger-800">{error}</p>
          </div>
        </div>
      )}

      {/* Stats principales — solo gobernanza */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
              <Building2 className="text-primary-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Bancas</p>
              <p className="text-2xl font-bold">{data?.bancas.total ?? '—'}</p>
              <p className="text-xs text-success-600">{data?.bancas.activas ?? 0} activas</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Usuarios</p>
              <p className="text-2xl font-bold">{data?.usuarios.total ?? '—'}</p>
              <p className="text-xs text-success-600">{data?.usuarios.activos ?? 0} activos</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-success-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="text-success-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Suscripciones</p>
              <p className="text-2xl font-bold">{data?.suscripciones.activas ?? '—'}</p>
              <p className="text-xs text-gray-500">activas</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-warning-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="text-warning-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Por vencer</p>
              <p className="text-2xl font-bold text-warning-600">
                {data?.suscripciones.proximas_a_vencer ?? '—'}
              </p>
              <p className="text-xs text-gray-500">próximos 30 días</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Detalle de estados */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card>
          <CardHeader title="Bancas" subtitle="Estado de las bancas" />
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Total</span>
              <span className="font-bold">{data?.bancas.total ?? 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-success-50 rounded-lg">
              <span className="text-success-700">Activas</span>
              <Badge variant="success">{data?.bancas.activas ?? 0}</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-danger-50 rounded-lg">
              <span className="text-danger-700">Inactivas</span>
              <Badge variant="danger">{data?.bancas.inactivas ?? 0}</Badge>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader title="Usuarios" subtitle="Accesos al sistema" />
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Total</span>
              <span className="font-bold">{data?.usuarios.total ?? 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-success-50 rounded-lg">
              <span className="text-success-700">Activos</span>
              <Badge variant="success">{data?.usuarios.activos ?? 0}</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-danger-50 rounded-lg">
              <span className="text-danger-700">Bloqueados</span>
              <Badge variant="danger">{data?.usuarios.inactivos ?? 0}</Badge>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader title="Suscripciones" subtitle="Pagos de plataforma" />
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Total</span>
              <span className="font-bold">{data?.suscripciones.total ?? 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-success-50 rounded-lg">
              <span className="text-success-700">Activas</span>
              <Badge variant="success">{data?.suscripciones.activas ?? 0}</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-danger-50 rounded-lg">
              <span className="text-danger-700">Vencidas</span>
              <Badge variant="danger">{data?.suscripciones.vencidas ?? 0}</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-warning-50 rounded-lg">
              <span className="text-warning-700">Próximas a vencer</span>
              <Badge variant="warning">{data?.suscripciones.proximas_a_vencer ?? 0}</Badge>
            </div>
          </div>
        </Card>
      </div>

      {/* Info box */}
      <Card className="border-l-4 border-l-primary-500">
        <div className="flex items-start gap-3">
          <Shield className="text-primary-600 mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-gray-800">Rol ADMIN — Gobernanza</p>
            <ul className="text-sm text-gray-600 mt-1 space-y-1">
              <li>• ADMIN gobierna la plataforma, NO opera el negocio</li>
              <li>• Gestión de bancas, usuarios y suscripciones</li>
              <li>• Control de pagos y accesos</li>
              <li>• Si una banca vence → no puede operar</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminDashboardPage;
